import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Typography, IconButton, Chip, TextField, InputAdornment,
    Grid, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Alert,
    ToggleButton, ToggleButtonGroup, Stack, Tooltip, Fab, Divider,
} from '@mui/material';
import {
    Search as SearchIcon, Add as AddIcon, Edit as EditIcon,
    Delete as DeleteIcon, Email as EmailIcon, Business as BusinessIcon,
    Person as PersonIcon, TransferWithinAStation as ConvertIcon,
    Refresh as RefreshIcon, UploadFile as UploadFileIcon,
    Language as WebIcon, Facebook as FacebookIcon, LinkedIn as LinkedInIcon,
    Instagram as InstagramIcon, Phone as PhoneIcon, Handshake as HandshakeIcon,
    Campaign as CampaignIcon,
} from '@mui/icons-material';
import { TokenContext } from '../Context/TokenContext';
import { useNavigate } from 'react-router';
import PaysSelect from '../Componant/PaysSelect';
import AdresseForm from '../Componant/AdresseForm';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const STATUTS = [
    { value: 'chaud',    label: 'Chaud',    color: 'error' },
    { value: 'tiede',    label: 'Tiède',    color: 'warning' },
    { value: 'froid',    label: 'Froid',    color: 'info' },
    { value: 'mort',     label: 'Mort',     color: 'default' },
    { value: 'converti', label: 'Converti', color: 'success' },
];
const STATUT_MAP = Object.fromEntries(STATUTS.map(s => [s.value, s]));

const SOURCES = [
    { value: 'site_web',        label: 'Site web',          icon: <WebIcon sx={{ fontSize: 14 }} />,        color: 'primary' },
    { value: 'facebook',        label: 'Facebook',          icon: <FacebookIcon sx={{ fontSize: 14 }} />,   color: 'info' },
    { value: 'linkedin',        label: 'LinkedIn',          icon: <LinkedInIcon sx={{ fontSize: 14 }} />,   color: 'info' },
    { value: 'instagram',       label: 'Instagram',         icon: <InstagramIcon sx={{ fontSize: 14 }} />,  color: 'secondary' },
    { value: 'recommandation',  label: 'Recommandation',    icon: <HandshakeIcon sx={{ fontSize: 14 }} />,  color: 'success' },
    { value: 'salon',           label: 'Salon / Événement', icon: <CampaignIcon sx={{ fontSize: 14 }} />,   color: 'warning' },
    { value: 'cold_email',      label: 'Email sortant',     icon: <EmailIcon sx={{ fontSize: 14 }} />,      color: 'default' },
    { value: 'appel_entrant',   label: 'Appel entrant',     icon: <PhoneIcon sx={{ fontSize: 14 }} />,      color: 'success' },
    { value: 'autre',           label: 'Autre',             icon: null,                                     color: 'default' },
];
const SOURCE_MAP = Object.fromEntries(SOURCES.map(s => [s.value, s]));

const BESOINS = [
    { value: 'site_vitrine',       label: 'Site vitrine' },
    { value: 'ecommerce',          label: 'E-commerce' },
    { value: 'application_mobile', label: 'Application mobile' },
    { value: 'application_web',    label: 'Application web' },
    { value: 'logiciel',           label: 'Logiciel sur mesure' },
    { value: 'conseil',            label: 'Conseil / Stratégie' },
    { value: 'autre',              label: 'Autre' },
];
const BESOIN_MAP = Object.fromEntries(BESOINS.map(b => [b.value, b]));

const IMPORT_FIELDS = [
    { value: 'nom', label: 'Nom' },
    { value: 'prenom', label: 'Prénom' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Téléphone' },
    { value: 'type', label: 'Type (particulier/entreprise)' },
    { value: 'raison_sociale', label: 'Raison sociale' },
    { value: 'statut', label: 'Statut' },
    { value: 'source', label: 'Source' },
    { value: 'besoin', label: 'Besoin' },
    { value: 'budget', label: 'Budget' },
    { value: 'notes', label: 'Notes' },
    { value: 'site_web', label: 'Site web' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'rue', label: 'Rue' },
    { value: 'ville', label: 'Ville' },
    { value: 'code_postal', label: 'Code postal' },
    { value: 'pays', label: 'Pays' },
];

const emptyForm = {
    type_prospect: 'individual',
    nom: '', prenom: '', email: '', phone: '',
    raison_sociale: '',
    statut_prospect: 'froid',
    source_contact: '',
    besoin: '',
    budget_estime: '',
    notes: '',
    site_web: '', facebook: '', linkedin: '', instagram: '',
    rue: '', ville: '', code_postal: '', pays: 'France',
};

export default function ProspectsPage() {
    const { accessToken } = useContext(TokenContext);
    const navigate = useNavigate();

    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statutFilter, setStatutFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');

    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openConvert, setOpenConvert] = useState(false);
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [convertType, setConvertType] = useState('individual');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const [openImport, setOpenImport] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [fieldMapping, setFieldMapping] = useState({});
    const [showMapping, setShowMapping] = useState(false);

    const headers = { Authorization: `Bearer ${accessToken}` };
    const setF = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

    const fetchProspects = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true); setError(null);
        try {
            const params = statutFilter !== 'all' ? { statut: statutFilter } : {};
            const res = await axios.get(`${API_URL}/api/prospects`, { headers, params });
            setProspects(res.data || []);
        } catch {
            setError('Impossible de charger les prospects');
        } finally {
            setLoading(false);
        }
    }, [accessToken, statutFilter]);

    useEffect(() => { fetchProspects(); }, [fetchProspects]);

    const mapProspect = (p) => ({
        id: p.id_client,
        nom: p.type_client === 'company' ? (p.raison_sociale || p.nom || '—') : (p.nom || '—'),
        contact: p.Utilisateurs?.[0]
            ? `${p.Utilisateurs[0].prenom || ''} ${p.Utilisateurs[0].nom || ''}`.trim() || '—' : '—',
        email: p.Utilisateurs?.[0]?.email || '—',
        phone: p.Utilisateurs?.[0]?.phone || '—',
        typeProspect: p.type_client === 'company' ? 'Entreprise' : 'Particulier',
        statut: p.statut_prospect || 'froid',
        source_contact: p.source_contact || null,
        besoin: p.besoin || null,
        budget_estime: p.budget_estime || null,
        notes: p.notes || null,
        createdAt: p.date_creation ? new Date(p.date_creation).toLocaleDateString('fr-FR') : '—',
        _raw: p,
    });

    const mapped = prospects.map(mapProspect);

    const filtered = mapped.filter(p => {
        const matchSearch = !searchTerm
            || p.nom.toLowerCase().includes(searchTerm.toLowerCase())
            || p.contact.toLowerCase().includes(searchTerm.toLowerCase())
            || p.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchSource = sourceFilter === 'all' || p.source_contact === sourceFilter;
        return matchSearch && matchSource;
    });

    const stats = {
        total: prospects.length,
        chaud: prospects.filter(p => p.statut_prospect === 'chaud').length,
        tiede: prospects.filter(p => p.statut_prospect === 'tiede').length,
        froid: prospects.filter(p => p.statut_prospect === 'froid').length,
        mort: prospects.filter(p => p.statut_prospect === 'mort').length,
        converti: prospects.filter(p => p.statut_prospect === 'converti').length,
    };

    // ── Create ─────────────────────────────────────────────────────────────────
    const handleOpenCreate = () => { setForm(emptyForm); setSaveError(null); setOpenCreate(true); };

    const handleCreate = async () => {
        setSaving(true); setSaveError(null);
        try {
            if (!form.rue || !form.ville || !form.code_postal) {
                setSaveError('L\'adresse est obligatoire (rue, ville, code postal)');
                setSaving(false);
                return;
            }
            const payload = {
                type_prospect: form.type_prospect,
                nom: form.nom,
                raison_sociale: form.type_prospect === 'company' ? form.raison_sociale : null,
                statut_prospect: form.statut_prospect,
                source_contact: form.source_contact || null,
                besoin: form.besoin || null,
                budget_estime: form.budget_estime || null,
                notes: form.notes || null,
                site_web: form.site_web || null,
                facebook: form.facebook || null,
                linkedin: form.linkedin || null,
                instagram: form.instagram || null,
                utilisateurs: [{ nom: form.nom, prenom: form.prenom, email: form.email, phone: form.phone || null, is_administrator_account: true }],
                adresses: [{ rue: form.rue, ville: form.ville, code_postal: form.code_postal, pays: form.pays }],
            };
            await axios.post(`${API_URL}/api/prospects`, payload, { headers });
            setOpenCreate(false);
            fetchProspects();
        } catch (e) {
            setSaveError(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Edit ──────────────────────────────────────────────────────────────────
    const handleOpenEdit = (p) => {
        const raw = p._raw;
        setSelectedProspect(p);
        setForm({
            type_prospect: raw.type_client === 'company' ? 'company' : 'individual',
            nom: raw.Utilisateurs?.[0]?.nom || raw.nom || '',
            prenom: raw.Utilisateurs?.[0]?.prenom || '',
            email: raw.Utilisateurs?.[0]?.email || '',
            phone: raw.Utilisateurs?.[0]?.phone || '',
            raison_sociale: raw.raison_sociale || '',
            statut_prospect: raw.statut_prospect || 'froid',
            source_contact: raw.source_contact || '',
            besoin: raw.besoin || '',
            budget_estime: raw.budget_estime || '',
            notes: raw.notes || '',
            site_web: raw.site_web || '',
            facebook: raw.facebook || '',
            linkedin: raw.linkedin || '',
            instagram: raw.instagram || '',
            rue: raw.Adresses?.[0]?.rue || '',
            ville: raw.Adresses?.[0]?.ville || '',
            code_postal: raw.Adresses?.[0]?.code_postal || '',
            pays: raw.Adresses?.[0]?.pays || 'France',
        });
        setSaveError(null);
        setOpenEdit(true);
    };

    const handleEdit = async () => {
        setSaving(true); setSaveError(null);
        try {
            await axios.put(`${API_URL}/api/prospects/${selectedProspect.id}`, {
                nom: form.type_prospect === 'company' ? form.raison_sociale : form.nom,
                raison_sociale: form.type_prospect === 'company' ? form.raison_sociale : null,
                statut_prospect: form.statut_prospect,
                source_contact: form.source_contact || null,
                besoin: form.besoin || null,
                budget_estime: form.budget_estime || null,
                notes: form.notes || null,
                site_web: form.site_web || null,
                facebook: form.facebook || null,
                linkedin: form.linkedin || null,
                instagram: form.instagram || null,
            }, { headers });
            setOpenEdit(false);
            fetchProspects();
        } catch (e) {
            setSaveError(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Delete ─────────────────────────────────────────────────────────────────
    const handleDelete = async (p) => {
        if (!window.confirm(`Supprimer le prospect "${p.nom}" ?`)) return;
        try {
            await axios.delete(`${API_URL}/api/prospects/${p.id}`, { headers });
            fetchProspects();
        } catch { setError('Erreur lors de la suppression'); }
    };

    // ── Convert ────────────────────────────────────────────────────────────────
    const handleOpenConvert = (p) => { setSelectedProspect(p); setConvertType('individual'); setOpenConvert(true); };

    const handleConvert = async () => {
        setSaving(true); setSaveError(null);
        try {
            await axios.post(`${API_URL}/api/prospects/${selectedProspect.id}/convert`, { type_client: convertType }, { headers });
            setOpenConvert(false);
            fetchProspects();
            navigate('/clients');
        } catch (e) {
            setSaveError(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Import CSV / Excel ──────────────────────────────────────────────────────
    const handleOpenImport = () => {
        setImportFile(null); setImportError(null); setImportResult(null);
        setImportPreview(null); setFieldMapping({}); setShowMapping(false);
        setOpenImport(true);
    };

    const handleSelectImportFile = async (file) => {
        setImportFile(file);
        setImportResult(null); setImportError(null);
        setImportPreview(null); setFieldMapping({}); setShowMapping(false);
        if (!file) return;

        setPreviewLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API_URL}/api/prospects/import/preview`, formData, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' },
            });
            setImportPreview(res.data);
            setFieldMapping(res.data.suggestedMapping || {});
            const hasUnmapped = Object.values(res.data.suggestedMapping || {}).some(v => !v);
            setShowMapping(hasUnmapped);
        } catch (e) {
            // La prévisualisation est une aide facultative : si elle échoue, l'import direct reste possible.
            setImportPreview(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleMappingChange = (header, field) => {
        setFieldMapping(prev => ({ ...prev, [header]: field }));
    };

    const handleImport = async () => {
        if (!importFile) return;
        setImporting(true); setImportError(null); setImportResult(null);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            if (importPreview) formData.append('mapping', JSON.stringify(fieldMapping));
            const res = await axios.post(`${API_URL}/api/prospects/import`, formData, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' },
            });
            setImportResult(res.data);
            fetchProspects();
        } catch (e) {
            setImportError(e.response?.data?.error || e.message);
        } finally {
            setImporting(false);
        }
    };

    // ── Formulaire de prospection (partagé Créer / Modifier) ──────────────────
    const renderForm = () => (
        <Stack spacing={3} sx={{ mt: 1 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            {/* Type */}
            <Box>
                <Typography variant="subtitle2" gutterBottom>Type de prospect</Typography>
                <ToggleButtonGroup value={form.type_prospect} exclusive size="small"
                    onChange={(_, v) => v && setForm(f => ({ ...f, type_prospect: v }))}>
                    <ToggleButton value="individual"><PersonIcon sx={{ mr: 1 }} />Particulier</ToggleButton>
                    <ToggleButton value="company"><BusinessIcon sx={{ mr: 1 }} />Entreprise</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Grid container spacing={4}>
                {/* ── Colonne gauche : Identité + Adresse ───────────────────────── */}
                <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>Identité</Typography>
                            <Grid container spacing={2}>
                                {form.type_prospect === 'company' && (
                                    <Grid item xs={12}>
                                        <TextField fullWidth required label="Raison sociale" value={form.raison_sociale} onChange={setF('raison_sociale')} />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label={form.type_prospect === 'company' ? 'Nom du contact' : 'Nom'} value={form.nom} onChange={setF('nom')} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Prénom" value={form.prenom} onChange={setF('prenom')} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Email" type="email" value={form.email} onChange={setF('email')} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Téléphone" value={form.phone} onChange={setF('phone')} />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>Adresse</Typography>
                            <AdresseForm
                                value={{ rue: form.rue, ville: form.ville, code_postal: form.code_postal }}
                                onChange={a => setForm(f => ({ ...f, rue: a.rue, ville: a.ville, code_postal: a.code_postal }))}
                                required
                            />
                            <Box sx={{ mt: 2, maxWidth: 240 }}>
                                <PaysSelect value={form.pays} onChange={setF('pays')} />
                            </Box>
                        </Box>
                    </Stack>
                </Grid>

                {/* ── Colonne droite : Prospection + Réseaux sociaux ────────────── */}
                <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                                Informations de prospection
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Statut</InputLabel>
                                        <Select value={form.statut_prospect} onChange={setF('statut_prospect')} label="Statut">
                                            {STATUTS.filter(s => s.value !== 'converti').map(s =>
                                                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Source de contact</InputLabel>
                                        <Select value={form.source_contact} onChange={setF('source_contact')} label="Source de contact">
                                            <MenuItem value=""><em>— Non renseigné</em></MenuItem>
                                            {SOURCES.map(s =>
                                                <MenuItem key={s.value} value={s.value}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {s.icon}{s.label}
                                                    </Box>
                                                </MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Besoin principal</InputLabel>
                                        <Select value={form.besoin} onChange={setF('besoin')} label="Besoin principal">
                                            <MenuItem value=""><em>— Non renseigné</em></MenuItem>
                                            {BESOINS.map(b =>
                                                <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Budget estimé" placeholder="ex : 3 000 – 8 000 €"
                                        value={form.budget_estime} onChange={setF('budget_estime')} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth multiline minRows={3} label="Notes de prospection"
                                        placeholder="Contexte, historique, points importants..."
                                        value={form.notes} onChange={setF('notes')} />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>Réseaux sociaux</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Site web" placeholder="https://..."
                                        value={form.site_web} onChange={setF('site_web')}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><WebIcon fontSize="small" /></InputAdornment> }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Facebook" placeholder="https://facebook.com/..."
                                        value={form.facebook} onChange={setF('facebook')}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><FacebookIcon fontSize="small" /></InputAdornment> }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="LinkedIn" placeholder="https://linkedin.com/..."
                                        value={form.linkedin} onChange={setF('linkedin')}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><LinkedInIcon fontSize="small" /></InputAdornment> }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Instagram" placeholder="https://instagram.com/..."
                                        value={form.instagram} onChange={setF('instagram')}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><InstagramIcon fontSize="small" /></InputAdornment> }} />
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </Stack>
    );

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Gestion des Prospects
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                    { label: 'Total', value: stats.total, color: 'primary.main' },
                    { label: 'Chauds', value: stats.chaud, color: 'error.main' },
                    { label: 'Tièdes', value: stats.tiede, color: 'warning.main' },
                    { label: 'Froids', value: stats.froid, color: 'info.main' },
                    { label: 'Convertis', value: stats.converti, color: 'success.main' },
                ].map(s => (
                    <Grid item xs={6} sm={4} md={2} key={s.label}>
                        <Card>
                            <CardContent sx={{ py: 1.5 }}>
                                <Typography color="textSecondary" variant="body2">{s.label}</Typography>
                                <Typography variant="h5" sx={{ color: s.color }}>{loading ? '…' : s.value}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Filtres */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    placeholder="Rechercher un prospect..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 200 } }}
                />
                <FormControl sx={{ minWidth: { xs: '100%', sm: 130 } }}>
                    <InputLabel>Statut</InputLabel>
                    <Select value={statutFilter} onChange={e => setStatutFilter(e.target.value)} label="Statut">
                        <MenuItem value="all">Tous</MenuItem>
                        {STATUTS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                    <InputLabel>Source</InputLabel>
                    <Select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} label="Source">
                        <MenuItem value="all">Toutes</MenuItem>
                        {SOURCES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button variant="outlined" color="primary" startIcon={<UploadFileIcon />} onClick={handleOpenImport}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    Importer
                </Button>
                <Tooltip title="Actualiser">
                    <IconButton onClick={fetchProspects} color="primary">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Table */}
            <Paper variant="outlined">
                <TableContainer>
                    <Table size="small" sx={{ minWidth: 750 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Prospect</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell>Besoin</TableCell>
                                <TableCell>Budget</TableCell>
                                <TableCell>Statut</TableCell>
                                <TableCell>Créé le</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary">Aucun prospect trouvé</Typography></TableCell></TableRow>
                            ) : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(p => {
                                const srcCfg = SOURCE_MAP[p.source_contact];
                                const besoinCfg = BESOIN_MAP[p.besoin];
                                const statutCfg = STATUT_MAP[p.statut] || { label: p.statut, color: 'default' };
                                return (
                                    <TableRow key={p.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {p.typeProspect === 'Entreprise'
                                                    ? <BusinessIcon color="primary" sx={{ fontSize: 16 }} />
                                                    : <PersonIcon color="primary" sx={{ fontSize: 16 }} />}
                                                <Box>
                                                    <Typography variant="body2" fontWeight={500}>{p.nom}</Typography>
                                                    {p.notes && (
                                                        <Tooltip title={p.notes} placement="bottom-start">
                                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160, display: 'block', cursor: 'help' }}>
                                                                📝 {p.notes}
                                                            </Typography>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{p.contact}</Typography>
                                            {p.email !== '—' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmailIcon sx={{ fontSize: 11, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary">{p.email}</Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {srcCfg ? (
                                                <Chip
                                                    icon={srcCfg.icon || undefined}
                                                    label={srcCfg.label}
                                                    color={srcCfg.color}
                                                    size="small" variant="outlined"
                                                />
                                            ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            {besoinCfg
                                                ? <Chip label={besoinCfg.label} size="small" variant="outlined" />
                                                : <Typography variant="caption" color="text.disabled">—</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            {p.budget_estime
                                                ? <Typography variant="body2" fontWeight={500} color="success.dark">{p.budget_estime}</Typography>
                                                : <Typography variant="caption" color="text.disabled">—</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={statutCfg.label} color={statutCfg.color} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{p.createdAt}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Modifier">
                                                <IconButton size="small" onClick={() => handleOpenEdit(p)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {p.statut !== 'converti' && (
                                                <Tooltip title="Convertir en client">
                                                    <IconButton size="small" onClick={() => handleOpenConvert(p)} sx={{ color: 'success.main' }}>
                                                        <ConvertIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Supprimer">
                                                <IconButton size="small" onClick={() => handleDelete(p)} sx={{ color: 'error.main' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]} component="div"
                    count={filtered.length} rowsPerPage={rowsPerPage} page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            <Fab sx={{ position: 'fixed', bottom: 16, right: 16 }} color="primary"
                onClick={handleOpenCreate}>
                <AddIcon />
            </Fab>

            {/* Dialog Créer */}
            <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Nouveau prospect</DialogTitle>
                <DialogContent>{renderForm()}</DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreate(false)}>Annuler</Button>
                    <Button variant="contained" color="primary" onClick={handleCreate} disabled={saving}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Modifier */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Modifier le prospect</DialogTitle>
                <DialogContent>{renderForm()}</DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEdit(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleEdit} disabled={saving}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Convertir */}
            <Dialog open={openConvert} onClose={() => setOpenConvert(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Convertir en client</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {saveError && <Alert severity="error">{saveError}</Alert>}
                        <Typography variant="body2" color="text.secondary">
                            Le prospect <strong>{selectedProspect?.nom}</strong> sera converti en client.
                            Son statut passera à "Converti".
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel>Type de client</InputLabel>
                            <Select value={convertType} onChange={e => setConvertType(e.target.value)} label="Type de client">
                                <MenuItem value="individual">Particulier</MenuItem>
                                <MenuItem value="company">Entreprise</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConvert(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleConvert} disabled={saving}
                        sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Convertir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Importer */}
            <Dialog open={openImport} onClose={() => !importing && setOpenImport(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Importer des prospects (CSV / Excel)</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {importError && <Alert severity="error">{importError}</Alert>}

                        <Typography variant="body2" color="text.secondary">
                            Colonnes reconnues : Nom, Prénom, Email, Téléphone, Type (particulier/entreprise),
                            Raison sociale, Statut, Source, Besoin, Budget, Notes, Site web, Facebook, LinkedIn,
                            Instagram, Rue, Ville, Code postal, Pays. Les noms de colonnes sont insensibles à la
                            casse et aux accents.
                        </Typography>

                        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}
                            sx={{ borderColor: 'primary.main', color: 'primary.main' }}>
                            {importFile ? importFile.name : 'Choisir un fichier'}
                            <input type="file" accept=".csv,.xlsx,.xls" hidden
                                onChange={e => handleSelectImportFile(e.target.files?.[0] || null)} />
                        </Button>

                        {previewLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">Analyse du fichier...</Typography>
                            </Box>
                        )}

                        {importPreview && (
                            <Box>
                                <Button
                                    size="small"
                                    onClick={() => setShowMapping(v => !v)}
                                    sx={{ color: 'primary.main', textTransform: 'none', px: 0 }}
                                >
                                    {showMapping ? 'Masquer le mapping des champs' : 'Mapper les champs manuellement (si nécessaire)'}
                                </Button>

                                {showMapping && (
                                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Associez chaque colonne détectée dans votre fichier au champ prospect correspondant.
                                        </Typography>
                                        {importPreview.headers.map(header => (
                                            <FormControl key={header} fullWidth size="small">
                                                <InputLabel>{header}</InputLabel>
                                                <Select
                                                    label={header}
                                                    value={fieldMapping[header] || ''}
                                                    onChange={e => handleMappingChange(header, e.target.value)}
                                                >
                                                    <MenuItem value="">Ignorer cette colonne</MenuItem>
                                                    {IMPORT_FIELDS.map(f => (
                                                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        )}

                        {importResult && (
                            <Alert severity={importResult.failed > 0 ? 'warning' : 'success'}>
                                {importResult.imported} prospect(s) importé(s) sur {importResult.total}
                                {importResult.failed > 0 && ` — ${importResult.failed} échec(s)`}
                            </Alert>
                        )}

                        {importResult?.errors?.length > 0 && (
                            <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                                {importResult.errors.map((err, i) => (
                                    <Typography key={i} variant="caption" color="error" sx={{ display: 'block' }}>
                                        Ligne {err.line} : {err.message}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenImport(false)} disabled={importing}>Fermer</Button>
                    <Button variant="contained" onClick={handleImport} disabled={importing || !importFile}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {importing ? <CircularProgress size={20} color="inherit" /> : 'Importer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
